/**
 * Sprint P32 · Extracted from BlockModelWizardStep.jsx.
 * Drillhole influence / anisotropic search-ellipsoid controls +
 * per-collar exclusion buffer + domain / lithology constraints.
 */
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";


function NumField({ label, value, onChange, step = 1, testId, disabled = false }) {
  return (
    <div>
      <Label className="text-[10px] font-semibold text-slate-600">{label}</Label>
      <Input type="number" step={step} value={value ?? ""}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value === "" ? NaN : Number(e.target.value))}
              className="h-8 text-[12px] font-mono"
              data-testid={testId} />
    </div>
  );
}


export default function InfluencePanel({ influence, setInfluence, projectRadius }) {
  const [open, setOpen] = useState(false);
  const set = (k) => (v) => setInfluence((inf) => ({
    ...inf, [k]: v, _userTouched: true,
  }));
  const isEllipsoid = influence.kind === "ellipsoid";

  return (
    <div className="rounded border border-slate-200 bg-slate-50/50"
         data-testid="w-bm-influence-panel">
      <button onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-slate-600 hover:bg-slate-100 rounded"
              data-testid="w-bm-influence-toggle">
        <span className="flex items-center gap-2">
          {open ? "▾" : "▸"} Drillhole influence &amp; search
        </span>
        <span className="text-[10px] text-slate-500 normal-case tracking-normal">
          {influence.kind === "circular"
            ? `Circular · r = ${influence.search_radius} m`
            : `Ellipsoid · ${influence.search_rx} / ${influence.search_ry} / ${influence.search_rz} m`}
          {influence.borehole_influence_radius > 0
            ? ` · buffer ${influence.borehole_influence_radius} m` : ""}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3 border-t border-slate-200">
          {projectRadius > 0 && !influence._userTouched && (
            <div className="rounded border border-sky-200 bg-sky-50 text-[10px] font-mono text-sky-900 px-2 py-1 mt-2">
              Using project-setup default influence: <b>{projectRadius} m</b>
            </div>
          )}

          <div>
            <Label className="text-[10px] font-semibold text-slate-600">Buffer type</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[
                { v: "circular",  l: "Circular / Isotropic" },
                { v: "ellipsoid", l: "Ellipsoid / Anisotropic" },
              ].map((o) => (
                <button key={o.v} onClick={() => set("kind")(o.v)}
                        className={`h-8 rounded border text-[11px] ${
                          influence.kind === o.v
                            ? "border-brand-blue bg-brand-blue/10 text-brand-blue font-semibold"
                            : "border-slate-300 text-slate-700"
                        }`}
                        data-testid={`w-bm-buffer-${o.v}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          {!isEllipsoid && (
            <NumField label="Search radius (m)" value={influence.search_radius}
                      onChange={set("search_radius")} step="1"
                      testId="w-bm-search-radius" />
          )}

          {isEllipsoid && (
            <>
              <div>
                <Label className="text-[10px] font-semibold text-slate-600">
                  Ellipsoid axes (m) · along-strike / across-strike / vertical
                </Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <NumField label="Rx (strike)" value={influence.search_rx}
                             onChange={set("search_rx")} step="1"
                             testId="w-bm-search-rx" />
                  <NumField label="Ry (dip)"    value={influence.search_ry}
                             onChange={set("search_ry")} step="1"
                             testId="w-bm-search-ry" />
                  <NumField label="Rz (vertical)" value={influence.search_rz}
                             onChange={set("search_rz")} step="1"
                             testId="w-bm-search-rz" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-semibold text-slate-600">
                  Orientation (degrees · mining convention)
                </Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <NumField label="Azimuth" value={influence.azimuth}
                             onChange={set("azimuth")} step="1"
                             testId="w-bm-azimuth" />
                  <NumField label="Plunge"  value={influence.plunge}
                             onChange={set("plunge")}  step="1"
                             testId="w-bm-plunge" />
                  <NumField label="Dip"     value={influence.dip}
                             onChange={set("dip")}     step="1"
                             testId="w-bm-dip" />
                </div>
              </div>
            </>
          )}

          <NumField label="Per-collar exclusion buffer (m) — 0 to disable"
                      value={influence.borehole_influence_radius}
                      onChange={set("borehole_influence_radius")} step="1"
                      testId="w-bm-collar-buffer" />

          <div className="grid grid-cols-2 gap-2">
            <NumField label="Max samples per estimate"
                       value={influence.max_samples}
                       onChange={set("max_samples")} step="1"
                       testId="w-bm-max-samples" />
            <NumField label="Min samples per estimate"
                       value={influence.min_samples}
                       onChange={set("min_samples")} step="1"
                       testId="w-bm-min-samples" />
          </div>

          <div className="grid grid-cols-1 gap-1.5 pt-1 border-t border-slate-200">
            <label className="inline-flex items-center gap-2 text-[11px] cursor-pointer">
              <Checkbox checked={influence.domain_aware}
                          onCheckedChange={(v) => set("domain_aware")(!!v)}
                          data-testid="w-bm-domain-aware" />
              <span>Domain-constrained (only use samples in the block's domain)</span>
            </label>
            {influence.domain_aware && (
              <label className="inline-flex items-center gap-2 text-[10px] cursor-pointer pl-5 text-slate-600">
                <Checkbox checked={influence.hard_boundary}
                            onCheckedChange={(v) => set("hard_boundary")(!!v)}
                            data-testid="w-bm-hard-boundary" />
                <span>Hard boundary (strict) — reject cross-domain samples entirely</span>
              </label>
            )}
            <label className="inline-flex items-center gap-2 text-[11px] cursor-pointer">
              <Checkbox checked={influence.by_lithology}
                          onCheckedChange={(v) => set("by_lithology")(!!v)}
                          data-testid="w-bm-by-lithology" />
              <span>Lithology-constrained (separate estimates per lithology)</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
